import { type LWApiPublicAssetListParamsRawI } from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import { type AssetDetails } from 'modules/queries/asset/useDetails';
import useAssetList from 'modules/queries/asset/useList';
import useVmWareVCloudVdcPortalDetailsForVdcs from 'modules/queries/vmWare/vCloud/vdc/usePortalDetailsForVdcs';

interface UseServerListNestingOptions {
	enabled: boolean;
	/** The list's own `alsowith`, so fetched children render like any other row. */
	alsowith: LWApiPublicAssetListParamsRawI['alsowith'];
}

/**
 * The data nesting needs beyond the loaded page(s) of the servers list: the
 * VM ids of each vDC on screen, and the children the parents point to that
 * haven't been loaded by the list yet.
 */
const useServerListNesting = (
	servers: AssetDetails[],
	{ enabled, alsowith }: UseServerListNestingOptions,
) => {
	const vdcIds = servers
		.filter((server) => server.isVmware)
		.map((server) => server.uniq_id);

	const {
		data: linkedChildIds,
		isLoading: isLoadingVdcChildren,
		isError: isVdcChildrenError,
	} = useVmWareVCloudVdcPortalDetailsForVdcs(vdcIds, { enabled });

	const referencedIds = new Set<string>();

	servers
		.filter((server) => server.isPrivateParent)
		.forEach((server) =>
			server.children?.forEach((child) => referencedIds.add(child.uniq_id)),
		);
	Object.values(linkedChildIds).forEach((childIds) =>
		childIds.forEach((childId) => referencedIds.add(childId)),
	);

	const loadedIds = new Set(servers.map((server) => server.uniq_id));
	const missingIds = Array.from(referencedIds)
		.filter((childId) => !loadedIds.has(childId))
		.sort();

	const {
		data: missingServers,
		isInitialLoading: isLoadingMissingServers,
		isError: isMissingServersError,
	} = useAssetList(
		{
			uniq_id: missingIds,
			alsowith,
			page_size: missingIds.length,
		},
		{
			enabled: enabled && missingIds.length > 0,
			refetchInterval: 30000,
			keepPreviousData: true,
		},
	);

	return {
		linkedChildIds,
		// keepPreviousData can hold a child that is no longer referenced
		extraServers: (missingServers?.items ?? []).filter((server) =>
			referencedIds.has(server.uniq_id),
		),
		isLoading: isLoadingVdcChildren || isLoadingMissingServers,
		isError: isVdcChildrenError || isMissingServersError,
	};
};

export default useServerListNesting;
